import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { useState } from 'react';
import { Box, Divider, ListItemIcon, ListItemText, Typography, useTheme } from '@mui/material';
import {
  Hotel,
  Business,
  Category,
  LocationOn,
  Store,
  Assessment,
  Settings
} from '@mui/icons-material';
import { Can } from '@casl/react';

import { DataBox } from '@frontend/features/common/components/DataBox';
import { SideList, SideListItemButton } from '@frontend/features/common/components/SideList';
import { useAuth } from '@frontend/features/auth/hooks/useAuth';
import { BackButton } from '@frontend/features/common/components/BackButton';
import { IconAvatar } from '@frontend/features/common/components/IconAvatar';
import { useGetJobsQuery } from '@frontend/features/hotel-content-manager/queries';
import { JobsModal } from '@frontend/features/hotel-content-manager/components/Jobs';
import { routes } from '../routes';

export const HotelContentView = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { ability } = useAuth();
  const [jobsModalOpen, setJobsModalOpen] = useState(false);

  const { data } = useGetJobsQuery({
    page: 1,
    limit: 10,
    status: 'processing',
  });
  const totalJobs = data?.total || 0;

  const isViewPage = false; // This will control layout behavior similar to agency view

  const handleOpenJobsModal = () => {
    setJobsModalOpen(true);
  };

  const handleCloseJobsModal = () => {
    setJobsModalOpen(false);
  };

  return (
    <Box
      display="flex"
      gap={isViewPage ? 0 : 8}
      sx={{
        height: 'calc(100vh - 64px)',
        backgroundColor: theme.palette.paper.dark,
      }}
      overflow="hidden"
    >
      <OverlayScrollbarsComponent
        style={{
          minWidth: 300,
          backgroundColor: theme.palette.paper.dark,
          boxSizing: 'border-box',
          height: '100%',
          boxShadow: theme.shadows[1],
        }}
        className="overlayscrollbars-react"
        options={{ scrollbars: { autoHide: 'scroll', theme: 'os-theme-light' } }}
        defer
      >
        <Box display="flex" flexDirection="column" alignItems="flex-start" alignSelf="stretch" p={2} gap={3}>
          <Box display="flex" gap={1} alignItems="center" mt={1}>
            <IconAvatar size="medium" backgroundColor={theme.palette.divider}>
              <Hotel htmlColor={theme.palette.common.white} />
            </IconAvatar>
            <DataBox
              sx={{
                gap: 0,
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
                <Typography variant="subtitle2" color="text.primary">
                  {t('hotelContent.management.title')}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {t('hotelContent.management.activeJobs')}: <Typography component="span" variant="body2" color="text.primary" fontWeight="medium">
                  {totalJobs}
                </Typography>
              </Typography>
              <Typography
                variant="caption"
                onClick={handleOpenJobsModal}
                sx={{
                  color: 'primary.main',
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                {t('hotelContent.management.viewJobs')}
              </Typography>
            </DataBox>
          </Box>

          <Box sx={{ width: '100%' }}>
            <Divider aria-hidden="true" textAlign="left" sx={{ mb: 3 }} />

            <SideList aria-label={`hotel content menu ${t('hotelContent.management.general')}`} dense disablePadding>
              <Can I="view" a="ContentManager" ability={ability}>

                <SideListItemButton
                  selected={Boolean(
                    location.pathname === routes.dashboard.hotelContentManagement.root,
                  )}
                  onClick={() =>
                    navigate(routes.dashboard.hotelContentManagement.root)
                  }
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: '35px' }}>
                    <Hotel />
                  </ListItemIcon>
                  <ListItemText primary={t('hotelContent.nimbuxHotels.title')} />
                </SideListItemButton>
                <Divider />
                <SideListItemButton
                  selected={Boolean(
                    location.pathname.includes(
                      `${routes.dashboard.hotelContentManagement.root}/${routes.dashboard.hotelContentManagement.suppliers}`,
                    ),
                  )}
                  onClick={() =>
                    navigate(
                      `${routes.dashboard.hotelContentManagement.root}/${routes.dashboard.hotelContentManagement.suppliers}`,
                    )
                  }
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: '35px' }}>
                    <Business />
                  </ListItemIcon>
                  <ListItemText primary={t('hotelContent.management.tabs.suppliers')} />
                </SideListItemButton>

                <SideListItemButton
                  selected={Boolean(
                    location.pathname.includes(
                      `${routes.dashboard.hotelContentManagement.root}/${routes.dashboard.hotelContentManagement.defaultSuppliers}`,
                    ),
                  )}
                  onClick={() =>
                    navigate(
                      `${routes.dashboard.hotelContentManagement.root}/${routes.dashboard.hotelContentManagement.defaultSuppliers}`,
                    )
                  }
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: '35px' }}>
                    <Settings />
                  </ListItemIcon>
                  <ListItemText primary={t('hotelContent.management.tabs.defaultSuppliers')} />
                </SideListItemButton>

                <SideListItemButton
                  selected={Boolean(
                    location.pathname.includes(
                      `${routes.dashboard.hotelContentManagement.root}/${routes.dashboard.hotelContentManagement.boards}`,
                    ),
                  )}
                  onClick={() =>
                    navigate(
                      `${routes.dashboard.hotelContentManagement.root}/${routes.dashboard.hotelContentManagement.boards}`,
                    )
                  }
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: '35px' }}>
                    <Assessment />
                  </ListItemIcon>
                  <ListItemText primary={t('hotelContent.management.tabs.boards')} />
                </SideListItemButton>

                <SideListItemButton
                  selected={Boolean(
                    location.pathname.includes(
                      `${routes.dashboard.hotelContentManagement.root}/${routes.dashboard.hotelContentManagement.categories}`,
                    ),
                  )}
                  onClick={() =>
                    navigate(
                      `${routes.dashboard.hotelContentManagement.root}/${routes.dashboard.hotelContentManagement.categories}`,
                    )
                  }
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: '35px' }}>
                    <Category />
                  </ListItemIcon>
                  <ListItemText primary={t('hotelContent.management.tabs.categories')} />
                </SideListItemButton>

                <SideListItemButton
                  selected={Boolean(
                    location.pathname.includes(
                      `${routes.dashboard.hotelContentManagement.root}/${routes.dashboard.hotelContentManagement.destinations}`,
                    ),
                  )}
                  onClick={() =>
                    navigate(
                      `${routes.dashboard.hotelContentManagement.root}/${routes.dashboard.hotelContentManagement.destinations}`,
                    )
                  }
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: '35px' }}>
                    <LocationOn />
                  </ListItemIcon>
                  <ListItemText primary={t('hotelContent.management.tabs.destinations')} />
                </SideListItemButton>
                <SideListItemButton
                  selected={Boolean(
                    location.pathname.includes(
                      `${routes.dashboard.hotelContentManagement.root}${routes.dashboard.hotelContentManagement.vendorHotels}`,
                    ),
                  )}
                  onClick={() =>
                    navigate(
                      `${routes.dashboard.hotelContentManagement.root}/${routes.dashboard.hotelContentManagement.vendorHotels}`,
                    )
                  }
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: '35px' }}>
                    <Store />
                  </ListItemIcon>
                  <ListItemText primary={t('hotelContent.management.tabs.vendorHotels')} />
                </SideListItemButton>
              </Can>
            </SideList>
          </Box>
        </Box>
      </OverlayScrollbarsComponent >

      {!isViewPage ? (
        <Box
          sx={{
            overflowX: 'auto',
            width: '100%',
            pr: 2,
            position: 'relative',
          }}
        >
          <Box
            sx={{
              mt: 2,
              mb: 2,
            }}
          >
            <BackButton backRoute={routes.dashboard.index} />
          </Box>

          <Outlet />
        </Box>
      ) : (
        <Box
          sx={{
            width: '100%',
          }}
        >
          <Outlet />
        </Box>
      )}

      {jobsModalOpen && <JobsModal open={jobsModalOpen} onClose={handleCloseJobsModal} />}
    </Box >
  );
};
